/**
 * Feature Flags — single-file, build-time feature gating.
 *
 * HOW IT WORKS
 * ─────────────
 * Each flag lists the environments where it is ENABLED.
 * Environment is auto-detected from VITE_API_BASE_URL at build time.
 * Local dev (`import.meta.env.DEV`) and test runs (Vitest) are treated as 'local'.
 *
 * LIFECYCLE
 * ─────────
 * 1. New feature  → add flag with ['local', 'qa']
 * 2. QA passes    → add 'staging'   → redeploy staging
 * 3. STG passes   → add 'production' → redeploy production
 * 4. Everywhere?  → DELETE the flag entry + remove all isEnabled() checks
 *                    from the codebase. Search: // TODO(feature-flag): <flagName>
 *
 * CLEANUP RULE
 * ────────────
 * If a flag is enabled in ALL environments it is dead weight.
 * Remove it in the SAME PR that promotes to production.
 * Search for  // TODO(feature-flag): <flagName>  to find every usage.
 *
 * ADDING A NEW FLAG
 * ─────────────────
 * 1. Add an entry to `flags` below.
 * 2. In every component that checks it, add a comment:
 *      // TODO(feature-flag): myFlagName — remove when flag is deleted
 * 3. Use:  import { isEnabled } from '~/src/featureFlags'
 *          if (!isEnabled('myFlagName')) return null
 *
 * REMOVING A FLAG (cleanup checklist)
 * ────────────────────────────────────
 * 1. Delete the entry from `flags` below.
 * 2. Search codebase for:  // TODO(feature-flag): <flagName>
 *    Remove every isEnabled() guard and the TODO comment.
 * 3. Update or remove any tests that were conditioned on the flag.
 *    Search tests for the flag name to find affected specs.
 * 4. Verify tests pass: npx vitest run
 */

type Environment = 'local' | 'qa' | 'staging' | 'production'

function detectEnvironment(): Environment {
  if (import.meta.env.DEV || import.meta.env.MODE === 'test') return 'local'

  const url = (import.meta.env.VITE_API_BASE_URL ?? '').toLowerCase()

  if (url.includes('qa')) return 'qa'
  if (url.includes('stg') || url.includes('nodedev')) return 'staging'

  return 'production'
}

export const currentEnv: Environment = detectEnvironment()

/**
 * Comma-separated flag names to force OFF (e.g. in .env.local-dev):
 * VITE_DISABLED_FEATURE_FLAGS=dashboardMetrics
 */
function getDisabledFlags(): Set<string> {
  const raw = import.meta.env.VITE_DISABLED_FEATURE_FLAGS ?? ''
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  )
}

const disabledFlags = getDisabledFlags()

/**
 * ┌─────────────────────────┬───────────────────────────────────────────┐
 * │ Flag name               │ Enabled environments                     │
 * ├─────────────────────────┼───────────────────────────────────────────┤
 * │ dashboardMetrics        │ ALL (incl. production)                   │
 * │ timeOffPolicies         │ local, qa, staging                       │
 * │ candidateTimeOff        │ local, qa, staging                       │
 * │ agencyTimeOffRequests   │ local, qa, staging                       │
 * │ timeOffHistoryYears     │ local, qa                                │
 * │ screeningAdminAndDates  │ local, qa                                │
 * │ mspBranchDropdown       │ local, qa                                │
 * │ orderAccessInvites      │ local, qa                                │
 * │ dashboardWelcomeCard    │ ALL (incl. production)                   │
 * │ manageAgenciesViewActivity │ ALL (incl. production)                │
 * │ employeeCandidateAuditLog │ ALL (incl. production)                 │
 * │ skillsCatalog           │ local, qa                                │
 * │ batchTableColumns       │ local, qa                                │
 * │ nestedDocumentFolders   │ local, qa                                │
 * └─────────────────────────┴───────────────────────────────────────────┘
 *
 * When a row has ALL four environments → delete it and clean up usages.
 */
const flags: Record<string, readonly Environment[]> = {
  // TODO(feature-flag): dashboardMetrics — enabled everywhere; delete flag + guards on the next cleanup pass
  dashboardMetrics: ['local', 'qa', 'staging', 'production'],
  // TODO(feature-flag): timeOffPolicies — remove when Global Time Off Policies is available in production
  timeOffPolicies: ['local', 'qa', 'staging'],
  // TODO(feature-flag): candidateTimeOff — remove when candidate-login Time Off is available in production
  candidateTimeOff: ['local', 'qa', 'staging'],
  // TODO(feature-flag): agencyTimeOffRequests — remove when agency request review is available in production
  agencyTimeOffRequests: ['local', 'qa', 'staging'],
  // TODO(feature-flag): timeOffHistoryYears — remove when the History year filter is in production (flag off: All dates / Last 30 days / Year to date). STFFLG-4310: the admin Assignment → Time Off and candidate My Info → Time Off History get a searchable year field (current year back to the hire year, then All; default the current year) and DS Selects for Type and Status. Kept mid-map so Release-1/qa end-of-map appends don't conflict.
  timeOffHistoryYears: ['local', 'qa', 'staging'],
  // TODO(feature-flag): screeningAdminAndDates — remove when the STFFLG-4170 screening card (status + date per field, Admin screening), the Interview tab's Admin item and date captions, and the client "Requires Admin" toggle are in production (flag off: today's 2-field card, no Requires Admin toggle). Kept mid-map so Release-1/qa end-of-map appends don't conflict.
  screeningAdminAndDates: ['local', 'qa'],
  // TODO(feature-flag): mspBranchDropdown — remove when the STFFLG-4230 MSP dashboard branch selection (a "Select branches" dropdown in the Selected branches KPI card, applied on Apply), the per-card KPI states and the compact Branch comparison header with its totals row are in production (flag off: today's branch chips, whole-strip KPI states and header). The removal PR also deletes BranchChipGroup and the legacy / compactHeader paths. Kept mid-map so Release-1/qa end-of-map appends don't conflict.
  mspBranchDropdown: ['local', 'qa'],
  // TODO(feature-flag): orderAccessInvites — remove when the STFFLG-4184 Job Order Access Part 2 invitations (the access popup's Invite tab and "Invited · other branch" section, and the Pending Orders invitation cards) are in production (flag off: today's popup with no tabs, and no invitation cards). mspplatform's flag of the same name must be on wherever this one is: deploy it first. Kept mid-map so Release-1/qa end-of-map appends don't conflict.
  orderAccessInvites: ['local', 'qa'],
  // TODO(feature-flag): dashboardWelcomeCard — remove when the dashboard welcome card (greeting, action row, period ring and bars, announcement line) is in production on the MSP and Agency dashboards (flag off: today's page header and Announcements panel, 18px section titles). mspplatform's flag of the same name must be on wherever this one is: deploy it first.
  dashboardWelcomeCard: ['local', 'qa', 'staging', 'production'],
  // TODO(feature-flag): manageAgenciesViewActivity — enabled everywhere; delete flag + guards on the next cleanup pass
  manageAgenciesViewActivity: ['local', 'qa', 'staging', 'production'],
  // TODO(feature-flag): employeeCandidateAuditLog — enabled everywhere; delete flag + guards on the next cleanup pass
  employeeCandidateAuditLog: ['local', 'qa', 'staging', 'production'],
  // TODO(feature-flag): skillsCatalog — remove when the Ollyboard-driven skills catalog is available in production
  skillsCatalog: ['local', 'qa'],
  // TODO(feature-flag): batchTableColumns — remove when the STFFLG-4179 Payroll › Batches figure columns, Columns control and DS toolbar are in production (flag off: today's table, and the list load never sends includeTotals)
  batchTableColumns: ['local', 'qa'],
  // TODO(feature-flag): nestedDocumentFolders — remove when STFFLG-4204 subfolders are in production.
  // Gates only the "New Folder" action INSIDE a folder. Showing subfolders the API returns is
  // always on. Pair with the backend flag of the same name — promote both together.
  nestedDocumentFolders: ['local', 'qa'],
} as const

/**
 * STFFLG-4184's flag, by constant: `isEnabled` answers false for a name it does not know, so a typo
 * would silently turn the feature off.
 */
export const ORDER_ACCESS_INVITES_FLAG = 'orderAccessInvites'

export function isEnabled(flag: keyof typeof flags): boolean {
  if (disabledFlags.has(flag)) return false
  const envs = flags[flag]
  if (!envs) return false
  return envs.includes(currentEnv)
}
